import MoreHorizIcon from "@mui/icons-material/MoreHorizRounded";
import { Box, Dialog, Grid, IconButton, InputAdornment, OutlinedInput, Skeleton } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type {
  ParameterDictionary,
  ParameterDictionaryValue,
  WorkbookFilter,
} from "../../../../../../api/types/workbookRuns.types";
import { ReportSource } from "../../../../../../api/types/workbookRuns.types";
import { toLabel } from "../../../../dashboard/utils/withValue";
import { useTextFiltersDictionaryItemsContext } from "../../../common/context/TextFiltersDictionaryItemsContext";
import WorkbookFilterItemLabel from "./WorkbookFilterItemLabel";
import WorkbookTextFilterValueSelector from "./WorkbookTextFilterValueSelector";

interface Props {
  filter: WorkbookFilter;
  showCompanyLogo: boolean;
  onUpdateFilterValue: (values: ParameterDictionary[]) => void;
}

const getWorkbookTextFilterValues = (workbookFilter: WorkbookFilter): ParameterDictionaryValue[] => {
  return (
    workbookFilter.selectedOptions?.map(({ properties }) => {
      return toLabel(properties[workbookFilter.dictionaryLoadSettings?.primaryKeyField ?? ""]);
    }) ?? []
  );
};

const WorkbookFilterItemText = ({ filter, showCompanyLogo, onUpdateFilterValue }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSelect, setShowSelect] = useState(false);
  const [dictionaryItems, setDictionaryItems] = useState<ParameterDictionary[]>([]);
  const { getDictionaryItems } = useTextFiltersDictionaryItemsContext();
  const value = useMemo(() => getWorkbookTextFilterValues(filter).join(", "), [filter]);

  useEffect(() => {
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setIsLoading(true);
    getDictionaryItems(
      filter.dictionaryLoadSettings?.parameterName ?? "",
      filter.clientCode ?? "",
      filter.dictionaryLoadSettings?.source ?? ReportSource.BI
    ).then((result) => {
      if (result) {
        setDictionaryItems(result);
      }
      setIsLoading(false);
    });
  }, [getDictionaryItems, filter]);

  const originalSelectedValues = useMemo(
    () =>
      filter.selectedOptions
        ?.map(({ properties }) => properties[filter.dictionaryLoadSettings?.primaryKeyField ?? ""])
        ?.filter((o) => o !== undefined) ?? [],
    [filter]
  );

  return (
    <>
      <WorkbookFilterItemLabel filter={filter} showCompanyLogo={showCompanyLogo} />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0 }}>
        <Grid container sx={{ flex: 1, position: "relative", gap: 1 }}>
          <OutlinedInput
            size="small"
            sx={{ flex: 1 }}
            value={value}
            disabled={isLoading}
            endAdornment={
              <InputAdornment position="end">
                <IconButton edge="end" onClick={() => setShowSelect(true)} disabled={isLoading}>
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            }
          />
          {isLoading && (
            <Grid container sx={{ flex: 1, position: "absolute", pointerEvents: "none" }}>
              <Skeleton
                animation="wave"
                variant="rectangular"
                sx={{ width: "100%", height: 35, bgcolor: "#ffffff1c" }}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      {showSelect && (
        <Dialog
          open
          onClose={() => setShowSelect(false)}
          slotProps={{ paper: { sx: (t) => ({ height: "100%", width: t.breakpoints.values.sm }) } }}
        >
          <WorkbookTextFilterValueSelector
            filterCaption={filter.name ?? ""}
            onBack={() => setShowSelect(false)}
            keyFieldName={filter.dictionaryLoadSettings?.primaryKeyField ?? ""}
            options={dictionaryItems}
            originalSelectedValues={originalSelectedValues}
            onUpdateFilter={onUpdateFilterValue}
            dictionarySource={filter.dictionaryLoadSettings?.source ?? ReportSource.BI}
          />
        </Dialog>
      )}
    </>
  );
};

export default WorkbookFilterItemText;
